import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { formatDateTime, formatMoney } from '../lib/utils';

type ActionType = 'percent' | 'fixed' | 'free_shipping' | 'tiered';

interface Promotion {
  id: string;
  name: string;
  type: ActionType;
  conditions: { markets?: string[]; minSubtotalMinor?: number };
  actions:
    | { type: 'percent'; percent: number }
    | { type: 'fixed'; amountMinor: number }
    | { type: 'free_shipping' }
    | { type: 'tiered'; tiers: { minSubtotalMinor: number; percent: number }[] };
  stackable: boolean;
  validFrom: string | null;
  validTo: string | null;
  usageLimit: number | null;
  usageCount: number;
  spentMinor: number;
  isActive: boolean;
  coupons: {
    code: string;
    usageCount: number;
    usageLimitPerCustomer: number | null;
    isActive: boolean;
  }[];
}

const euros = (value: string) => Math.round(Number(value.replace(',', '.')) * 100);
const toLocal = (iso: string | null) => (iso ? iso.slice(0, 16) : '');

function PromotionDialog({ promotion, onClose }: { promotion?: Promotion; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const a = promotion?.actions;
  const [name, setName] = useState(promotion?.name ?? '');
  const [type, setType] = useState<ActionType>(promotion?.type ?? 'percent');
  const [percent, setPercent] = useState(a?.type === 'percent' ? String(a.percent) : '10');
  const [amount, setAmount] = useState(a?.type === 'fixed' ? String(a.amountMinor / 100) : '10');
  const [tiers, setTiers] = useState(
    a?.type === 'tiered'
      ? a.tiers.map((x) => `${String(x.minSubtotalMinor / 100)}:${String(x.percent)}`).join('\n')
      : '100:5\n200:10',
  );
  const [minSubtotal, setMinSubtotal] = useState(
    promotion?.conditions.minSubtotalMinor
      ? String(promotion.conditions.minSubtotalMinor / 100)
      : '',
  );
  const [markets, setMarkets] = useState<string[]>(promotion?.conditions.markets ?? []);
  const [stackable, setStackable] = useState(promotion?.stackable ?? false);
  const [validFrom, setValidFrom] = useState(toLocal(promotion?.validFrom ?? null));
  const [validTo, setValidTo] = useState(toLocal(promotion?.validTo ?? null));
  const [usageLimit, setUsageLimit] = useState(
    promotion?.usageLimit ? String(promotion.usageLimit) : '',
  );
  const [codes, setCodes] = useState(
    (promotion?.coupons ?? [])
      .filter((c) => c.isActive)
      .map((c) => c.code)
      .join('\n'),
  );
  const [perCustomer, setPerCustomer] = useState(
    promotion?.coupons[0]?.usageLimitPerCustomer
      ? String(promotion.coupons[0].usageLimitPerCustomer)
      : '',
  );
  const [active, setActive] = useState(promotion?.isActive ?? true);
  const [error, setError] = useState<string>();

  const save = async () => {
    setError(undefined);
    const actions =
      type === 'percent'
        ? { type, percent: Number(percent) }
        : type === 'fixed'
          ? { type, amountMinor: euros(amount) }
          : type === 'tiered'
            ? {
                type,
                tiers: tiers
                  .split('\n')
                  .map((l) => l.split(':'))
                  .filter((p) => p.length === 2)
                  .map(([min, pct]) => ({
                    minSubtotalMinor: euros(min ?? '0'),
                    percent: Number(pct),
                  })),
              }
            : { type };
    const body = {
      name,
      actions,
      conditions: {
        ...(markets.length ? { markets } : {}),
        ...(minSubtotal ? { minSubtotalMinor: euros(minSubtotal) } : {}),
      },
      stackable,
      priority: 0,
      isActive: active,
      validFrom: validFrom ? new Date(validFrom).toISOString() : null,
      validTo: validTo ? new Date(validTo).toISOString() : null,
      usageLimit: usageLimit ? Number(usageLimit) : null,
      coupons: codes
        .split(/\s+/)
        .filter(Boolean)
        .map((code) => ({ code, usageLimitPerCustomer: perCustomer ? Number(perCustomer) : null })),
    };
    try {
      await api(promotion ? `/promotions/${promotion.id}` : '/promotions', {
        method: promotion ? 'PUT' : 'POST',
        body,
      });
      await queryClient.invalidateQueries({ queryKey: ['promotions'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const text = (label: string, value: string, set: (v: string) => void, type = 'text') => (
    <Field label={label}>
      {(id) => (
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => {
            set(e.target.value);
          }}
        />
      )}
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={promotion ? promotion.name : t('promo.new')}
      className="max-w-2xl"
      footer={
        <Button disabled={name.trim().length < 2} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm md:grid-cols-2">
        {text(t('promo.name'), name, setName)}
        <Field label={t('promo.type')}>
          {(id) => (
            <Select
              id={id}
              value={type}
              onChange={(e) => {
                setType(e.target.value as ActionType);
              }}
            >
              {(['percent', 'fixed', 'free_shipping', 'tiered'] as const).map((x) => (
                <option key={x} value={x}>
                  {t(`promo.types.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {type === 'percent' ? text(t('promo.percent'), percent, setPercent, 'number') : null}
        {type === 'fixed' ? text(t('promo.amount'), amount, setAmount) : null}
        {type === 'tiered' ? (
          <Field label={t('promo.tiers')} hint={t('promo.tiersHint')}>
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                value={tiers}
                onChange={(e) => {
                  setTiers(e.target.value);
                }}
              />
            )}
          </Field>
        ) : null}
        {text(t('promo.minSubtotal'), minSubtotal, setMinSubtotal)}
        <fieldset className="grid gap-1">
          <legend className="font-medium">{t('promo.markets')}</legend>
          <div className="flex gap-3">
            {['LV', 'LT', 'EE'].map((m) => (
              <label key={m} className="flex items-center gap-1">
                <Checkbox
                  checked={markets.includes(m)}
                  onChange={(e) => {
                    setMarkets(e.target.checked ? [...markets, m] : markets.filter((x) => x !== m));
                  }}
                />
                {m}
              </label>
            ))}
          </div>
        </fieldset>
        {text(t('promo.validFrom'), validFrom, setValidFrom, 'datetime-local')}
        {text(t('promo.validTo'), validTo, setValidTo, 'datetime-local')}
        {text(t('promo.usageLimit'), usageLimit, setUsageLimit, 'number')}
        <Field label={t('promo.codes')} hint={t('promo.codesHint')}>
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              value={codes}
              onChange={(e) => {
                setCodes(e.target.value);
              }}
              className="font-mono uppercase"
            />
          )}
        </Field>
        {text(t('promo.perCustomer'), perCustomer, setPerCustomer, 'number')}
        <div className="grid content-end gap-2">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={stackable}
              onChange={(e) => {
                setStackable(e.target.checked);
              }}
            />
            {t('promo.stackable')}
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={active}
              onChange={(e) => {
                setActive(e.target.checked);
              }}
            />
            {t('promo.active')}
          </label>
        </div>
        {error ? (
          <Alert variant="destructive" className="md:col-span-2">
            {error}
          </Alert>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Промо и купоны: автоматические скидки и коды, лимиты, сроки, расход. */
export function PromotionsPage() {
  const { t, i18n } = useTranslation();
  const list = useQuery({
    queryKey: ['promotions'],
    queryFn: () => api<{ items: Promotion[] }>('/promotions'),
  });
  const [editing, setEditing] = useState<Promotion | 'new'>();
  const describe = (p: Promotion) => {
    const a = p.actions;
    if (a.type === 'percent') return `−${String(a.percent)}%`;
    if (a.type === 'fixed') return `−${formatMoney(a.amountMinor, 'EUR', i18n.language)}`;
    if (a.type === 'tiered') return a.tiers.map((x) => `${String(x.percent)}%`).join(' / ');
    return t('promo.types.free_shipping');
  };

  return (
    <>
      <PageHeader
        title={t('promo.title')}
        description={t('promo.hint')}
        actions={
          <Button
            onClick={() => {
              setEditing('new');
            }}
          >
            {t('promo.new')}
          </Button>
        }
      />
      {list.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('promo.empty')}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        {(list.data?.items ?? []).map((p) => (
          <Card key={p.id}>
            <CardHeader
              title={p.name}
              description={`${describe(p)} · ${
                p.coupons.some((c) => c.isActive)
                  ? p.coupons
                      .filter((c) => c.isActive)
                      .map((c) => c.code)
                      .join(', ')
                  : t('promo.automatic')
              }`}
              actions={
                <Badge variant={p.isActive ? 'secondary' : 'outline'}>
                  {p.isActive ? t('promo.active') : t('promo.inactive')}
                </Badge>
              }
            />
            <CardContent className="grid gap-1 text-sm">
              <p className="text-muted-foreground">
                {t('promo.used', { count: p.usageCount })}
                {p.usageLimit ? ` / ${String(p.usageLimit)}` : ''} ·{' '}
                {t('promo.spent', { amount: formatMoney(p.spentMinor, 'EUR', i18n.language) })}
              </p>
              {p.validFrom || p.validTo ? (
                <p className="text-muted-foreground">
                  {p.validFrom ? formatDateTime(p.validFrom, i18n.language) : '…'} —{' '}
                  {p.validTo ? formatDateTime(p.validTo, i18n.language) : '…'}
                </p>
              ) : null}
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(p);
                  }}
                >
                  {t('common.edit')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {editing ? (
        <PromotionDialog
          {...(editing === 'new' ? {} : { promotion: editing })}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </>
  );
}
